import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import API from '../../services/apiService'
import './AdminStatsDashboard.css'

const Icons = {
  accounts: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  online: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>,
  characters: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="11" x2="14" y2="11"/></svg>,
  gms: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 17l3-7 4 4 5-10 4 7 4-4v11H2z"/></svg>,
  world: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><ellipse cx="12" cy="12" rx="4" ry="10"/><path d="M2 12h20"/></svg>,
  license: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>,
  users: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  chart: <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
}

function StatCard({ icon, value, label, sub, color, className }) {
  return (
    <div className={`stats-card ${className || ''}`} style={{ '--card-accent': color || 'var(--color-primary)' }}>
      <div className="stats-card__icon">{icon}</div>
      <div className="stats-card__body">
        <span className="stats-card__value">{value ?? '...'}</span>
        <span className="stats-card__label">{label}</span>
        {sub && <span className="stats-card__sub">{sub}</span>}
      </div>
    </div>
  )
}

function MiniStat({ label, value, color }) {
  return (
    <div className="stats-mini">
      <span className="stats-mini__value" style={{ color: color || 'var(--text-dark)' }}>{value ?? '...'}</span>
      <span className="stats-mini__label">{label}</span>
    </div>
  )
}

function BarChart({ data, valueKey, labelKey, maxValue, color }) {
  if (!data || data.length === 0) return <div className="stats-empty">{t('admin.noData')}</div>
  const max = maxValue || Math.max(...data.map(d => Number(d[valueKey]) || 0), 1)
  const barColor = color || 'var(--color-primary-dark)'
  return (
    <div className="stats-bars">
      {data.map((item, i) => {
        const val = Number(item[valueKey]) || 0
        const pct = (val / max) * 100
        return (
          <div key={i} className="stats-bar-row">
            <span className="stats-bar-label">{item[labelKey] ?? '?'}</span>
            <div className="stats-bar-track">
              <div className="stats-bar-fill" style={{ width: `${Math.max(pct, 2)}%`, background: barColor }} />
            </div>
            <span className="stats-bar-value">{val}</span>
          </div>
        )
      })}
    </div>
  )
}

function HorizontalBar({ value, max, label, color, reversed }) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div className="stats-hbar">
      <span className="stats-hbar-label">{label}</span>
      <div className="stats-hbar-track">
        <div
          className="stats-hbar-fill"
          style={{
            width: `${reversed ? 100 - pct : pct}%`,
            background: color || 'var(--color-primary-dark)',
          }}
        />
      </div>
      <span className="stats-hbar-value">{value}</span>
    </div>
  )
}

function Section({ title, icon, children, className }) {
  return (
    <div className={`stats-section ${className || ''}`}>
      <div className="stats-section-header">
        {icon && <span className="stats-section-icon">{icon}</span>}
        <h4 className="stats-section-title">{title}</h4>
      </div>
      <div className="stats-section-body">{children}</div>
    </div>
  )
}

function SkeletonBlock({ lines }) {
  return (
    <div className="stats-skeleton">
      {Array.from({ length: lines || 4 }).map((_, i) => (
        <div key={i} className="stats-skeleton-line" style={{ width: `${50 + Math.random() * 40}%` }} />
      ))}
    </div>
  )
}

export default function AdminStatsDashboard() {
  const { t } = useTranslation()
  const PERIODS = [
    { value: '24h', label: t('admin.period24h') },
    { value: '7d', label: t('admin.period7d') },
    { value: '30d', label: t('admin.period30d') },
    { value: 'all', label: t('admin.periodAll') },
  ]
  const [data, setData] = useState(null)
  const [worldData, setWorldData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [period, setPeriod] = useState('all')

  const loadStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [statsRes, worldRes] = await Promise.all([
        API.Admin.getStats(),
        API.Server.worldStats().catch(() => null)
      ])
      if (statsRes.success) {
        setData(statsRes.data)
      } else {
        setError(statsRes.error || 'Failed to load stats')
      }
      if (worldRes?.success) setWorldData(worldRes.data)
    } catch (err) {
      setError('Failed to connect to server')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadStats() }, [loadStats])

  const getCreatedCount = (counts) => {
    if (!counts) return null
    switch (period) {
      case '24h': return counts.createdToday
      case '7d': return counts.createdThisWeek
      case '30d': return counts.createdThisMonth
      default: return null
    }
  }

  if (error) {
    return (
      <div className="stats-container">
        <div className="stats-error">
          <span className="stats-error-icon">!</span>
          <span>{error}</span>
          <button className="stats-retry-btn" onClick={loadStats}>{t('account.retry')}</button>
        </div>
      </div>
    )
  }

  return (
    <div className="stats-container">
      <div className="stats-toolbar">
        <h3 className="stats-title">{t('admin.dashboard')}</h3>
        <div className="stats-period-filter">
          {PERIODS.map(p => (
            <button
              key={p.value}
              className={`stats-period-btn ${period === p.value ? 'active' : ''}`}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="stats-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="stats-card stats-card--skeleton">
              <SkeletonBlock lines={3} />
            </div>
          ))}
        </div>
      ) : data ? (
        <div className="stats-scroll">
          {/* ===== OVERVIEW ===== */}
          <Section title={t('admin.overview')} icon={Icons.chart}>
            <div className="stats-grid stats-grid--4">
              <StatCard
                icon={Icons.accounts}
                value={data.accounts?.total}
                label={t('admin.totalAccounts')}
                color="#3b82f6"
                className="stats-card--row"
              />
              <StatCard
                icon={Icons.online}
                value={data.sessions?.total}
                label={t('admin.onlineNow')}
                sub={`${data.sessions?.activeApp || 0} ${t('admin.app')} · ${data.sessions?.activeSite || 0} ${t('admin.site')}`}
                color="#22c55e"
                className="stats-card--row stats-card--live"
              />
              <StatCard
                icon={Icons.characters}
                value={data.characters?.total}
                label={t('admin.totalChars')}
                sub={`${data.characters?.unique || 0} ${t('admin.unique')} · ${data.characters?.online || 0} ${t('admin.online')}`}
                color="#a855f7"
                className="stats-card--row"
              />
              <StatCard
                icon={Icons.gms}
                value={data.gms?.total}
                label={t('admin.totalGMs')}
                sub={`${data.gms?.active || 0} ${t('admin.active')}`}
                color="#ef4444"
                className="stats-card--row"
              />
            </div>
          </Section>

          {/* ===== ACCOUNTS ===== */}
          <Section title={t('admin.accounts')} icon={Icons.accounts}>
            <div className="stats-grid stats-grid--2">
              <div className="stats-card">
                <div className="stats-card__title">{t('admin.statusBreakdown')}</div>
                <div className="stats-card__content">
                  <HorizontalBar value={data.accounts?.active || 0} max={data.accounts?.total || 1} label={t('account.active')} color="#22c55e" />
                  <HorizontalBar value={data.accounts?.inactive || 0} max={data.accounts?.total || 1} label={t('admin.gmInactive')} color="#6b7280" />
                  <HorizontalBar value={data.accounts?.suspended || 0} max={data.accounts?.total || 1} label={t('account.suspended')} color="#eab308" />
                  <HorizontalBar value={data.accounts?.banned || 0} max={data.accounts?.total || 1} label={t('account.banned')} color="#ef4444" />
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-card__title">{t('admin.accountInfo')}</div>
                <div className="stats-mini-grid">
                  <MiniStat label={t('admin.emailVerified')} value={data.accounts?.verifiedEmail} color="#22c55e" />
                  <MiniStat label={t('admin.withNickname')} value={data.accounts?.withNickname} color="#3b82f6" />
                  <MiniStat label={t('admin.withLicense')} value={data.accounts?.withLicense} color="#a855f7" />
                  {getCreatedCount(data.accounts) !== null && (
                    <MiniStat label={`${t('admin.created', { period })}`} value={getCreatedCount(data.accounts)} color="#eab308" />
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* ===== ONLINE ===== */}
          <Section title={t('admin.onlineActivity')} icon={Icons.online}>
            <div className="stats-grid stats-grid--3">
              <StatCard icon={Icons.online} value={data.sessions?.activeApp} label={t('admin.appOnline')} color="#22c55e" className="stats-card--row" />
              <StatCard icon={Icons.users} value={data.sessions?.activeSite} label={t('admin.siteOnline')} color="#3b82f6" className="stats-card--row" />
              <StatCard icon={Icons.characters} value={data.characters?.online} label={t('admin.charsOnline')} color="#a855f7" className="stats-card--row" />
            </div>
          </Section>

          {/* ===== CHARACTERS ===== */}
          <Section title={t('admin.characters')} icon={Icons.characters}>
            <div className="stats-grid stats-grid--2">
              <div className="stats-card">
                <div className="stats-card__title">
                  <span>{Icons.world}</span>
                  {t('admin.perWorld')}
                </div>
                <div className="stats-card__content">
                  {data.characters?.perWorld && data.characters.perWorld.length > 0 ? (
                    <BarChart
                      data={data.characters.perWorld}
                      valueKey="total"
                      labelKey="world"
                      color="linear-gradient(90deg, #c9a227, #ffd700)"
                    />
                  ) : (
                    <div className="stats-empty">{t('admin.noWorlds')}</div>
                  )}
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-card__title">{t('admin.characters')}</div>
                <div className="stats-mini-grid">
                  <MiniStat label={t('admin.avgPerAccount')} value={data.characters?.avgPerClient} color="#8b6914" />
                  <MiniStat label={t('admin.uniqueChars')} value={data.characters?.unique} color="#a855f7" />
                  <MiniStat label={t('admin.withoutChars')} value={data.characters?.clientsWithoutCharacters} color="#ef4444" />
                  {getCreatedCount(data.characters) !== null && (
                    <MiniStat label={`${t('admin.created', { period })}`} value={getCreatedCount(data.characters)} color="#eab308" />
                  )}
                  <MiniStat label={t('admin.onlineNow_')} value={data.characters?.online} color="#22c55e" />
                </div>
              </div>
            </div>
          </Section>

          {/* ===== WORLD ===== */}
          <Section title={t('admin.liveWorld')} icon={Icons.world}>
            {!worldData ? (
              <div className="stats-grid stats-grid--2">
                <div className="stats-card stats-card--skeleton"><SkeletonBlock lines={3} /></div>
                <div className="stats-card stats-card--skeleton"><SkeletonBlock lines={4} /></div>
              </div>
            ) : (
              <div className="stats-grid stats-grid--2">
                <div className="stats-card">
                  <div className="stats-card__title">{t('admin.population')}</div>
                  <div className="stats-mini-grid">
                    <MiniStat label={t('admin.totalCharacters')} value={worldData.totalCharacters} color="#3b82f6" />
                    <MiniStat label={t('admin.onlineNow')} value={worldData.onlineCharacters} color="#22c55e" />
                    <MiniStat label={t('admin.activeAccounts')} value={worldData.totalClientsWithChars} color="#a855f7" />
                  </div>
                </div>
                <div className="stats-card">
                  <div className="stats-card__title">{t('admin.perWorld')}</div>
                  <div className="stats-card__content">
                    {worldData.worldDistribution && worldData.worldDistribution.length > 0 ? (
                      <BarChart
                        data={worldData.worldDistribution}
                        valueKey="count"
                        labelKey="world"
                        color="linear-gradient(90deg, #3b82f6, #60a5fa)"
                      />
                    ) : (
                      <div className="stats-empty">{t('admin.noWorlds')}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* ===== LICENSES ===== */}
          <Section title={t('admin.licenses')} icon={Icons.license}>
            <div className="stats-grid stats-grid--2">
              <div className="stats-card">
                <div className="stats-card__title">{t('admin.licenseSummary')}</div>
                <div className="stats-mini-grid">
                  <MiniStat label={t('admin.licensesIssued')} value={data.licenses?.totalIssued} color="#8b6914" />
                  <MiniStat label={t('admin.permanentTabs')} value={data.licenses?.totalPermanentTabs} color="#eab308" />
                </div>
              </div>
              <div className="stats-card">
                <div className="stats-card__title">{t('admin.newTabPackages')}</div>
                <div className="stats-mini-grid">
                  <MiniStat label={t('admin.totalPackages')} value={data.newtabs?.total} color="#8b6914" />
                  <MiniStat label={t('admin.activePackages')} value={data.newtabs?.active} color="#22c55e" />
                  <MiniStat label={t('admin.expired')} value={data.newtabs?.expired} color="#ef4444" />
                  <MiniStat label={t('admin.activeQuantity')} value={data.newtabs?.activeQuantity} color="#3b82f6" />
                </div>
              </div>
            </div>
          </Section>

          {/* ===== GMs ===== */}
          <Section title={t('admin.gameMasters')} icon={Icons.gms}>
            <div className="stats-grid stats-grid--3">
              <StatCard icon={Icons.gms} value={data.gms?.active} label={t('admin.activeGMs')} color="#22c55e" sub={`${t('admin.of')} ${data.gms?.total || 0} ${t('admin.totalGMs').toLowerCase()}`} className="stats-card--row" />
              <StatCard icon={Icons.gms} value={data.gms?.activeToday} label={t('admin.seenToday')} color="#eab308" className="stats-card--row" />
              <StatCard icon={Icons.gms} value={data.gms?.activeWeek} label={t('admin.seenThisWeek')} color="#a855f7" sub={`${data.gms?.seenLast24h || 0} ${t('admin.in24h')}`} className="stats-card--row" />
            </div>
          </Section>
        </div>
      ) : null}
    </div>
  )
}